import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Modal, ModalHeader, ModalBody, ModalFooterWithButtons } from '@evertrue/et-components';
import sanitizeHtml from '../../utils/sanitize-html';

/**
 * Shared modal for picking an email template from the outreach library.
 * Split-pane layout: left = search + list, right = preview of selected template.
 */
const TemplatePickerModal = ({ isOpen, onClose, templates, onSelect, currentTemplateId }) => {
  const [selectedId, setSelectedId] = useState(currentTemplateId || null);
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = (templates || []).filter((t) => {
    const name = (t.templateName || '').toLowerCase();
    const sub = (t.templateSubject || '').toLowerCase();
    const q = searchQuery.toLowerCase();
    return name.includes(q) || sub.includes(q);
  });

  const selectedTemplate = templates?.find((t) => t.id === selectedId) || null;

  return (
    <Modal isOpen={isOpen} closeModal={onClose} size="large">
      <ModalHeader title="Select Email Template" closeModal={onClose} />
      <ModalBody>
        <div className="template-picker">
          <div className="template-picker--left">
            <div className="template-picker--search">
              <Icon icon="search" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="template-picker--search-input"
              />
              {searchQuery && (
                <button type="button" className="template-picker--search-clear" onClick={() => setSearchQuery('')}>
                  <Icon icon="close" />
                </button>
              )}
            </div>
            <div className="template-picker--count">
              {filtered.length} template{filtered.length !== 1 ? 's' : ''}
            </div>
            <div className="template-picker--list">
              {filtered.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`template-picker--item ${selectedId === t.id ? 'is-selected' : ''}`}
                  onClick={() => setSelectedId(t.id)}
                >
                  <div className="template-picker--item-icon">
                    <Icon icon="email" />
                  </div>
                  <div className="template-picker--item-info">
                    <span className="template-picker--item-name">{t.templateName}</span>
                    {t.templateSubject && <span className="template-picker--item-subject">{t.templateSubject}</span>}
                  </div>
                  {selectedId === t.id && <Icon icon="check" className="template-picker--item-check" />}
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="template-picker--empty">
                  <Icon icon="email" />
                  <p>{searchQuery ? 'No templates match your search' : 'No templates available'}</p>
                </div>
              )}
            </div>
          </div>

          <div className="template-picker--right">
            {selectedTemplate ? (
              <div className="template-picker--preview">
                <div className="template-picker--preview-header">
                  <h4 className="template-picker--preview-title">{selectedTemplate.templateName}</h4>
                  {selectedTemplate.templateSubject && (
                    <div className="template-picker--preview-subject">
                      <span className="template-picker--preview-subject-label">Subject:</span>
                      <span>{selectedTemplate.templateSubject}</span>
                    </div>
                  )}
                </div>
                <div className="template-picker--preview-body">
                  {selectedTemplate.templateBody ? (
                    <div
                      className="template-picker--preview-content"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedTemplate.templateBody) }}
                    />
                  ) : (
                    <div className="template-picker--preview-placeholder">
                      <Icon icon="email" />
                      <p>No body content</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="template-picker--preview-empty">
                <Icon icon="email" />
                <p>Select a template to preview</p>
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooterWithButtons>
        <Button type="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="action"
          disabled={!selectedId}
          onClick={() => {
            const t = templates.find((tpl) => tpl.id === selectedId);
            if (t) onSelect(t);
          }}
        >
          Use Template
        </Button>
      </ModalFooterWithButtons>
    </Modal>
  );
};

TemplatePickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  templates: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  currentTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default TemplatePickerModal;
